<template>
  <div class="flexing flex-col w-full">
    <form class="w-full p-6 space-y-6" @submit.prevent>
      <h1
        v-if="context !== LoginFormContext.CONNECTION"
        class="text-texts-standard-important font-medium text-36"
      >
        {{ $t('homepage.loginOrReg') }}
      </h1>
      <div
        v-if="context !== LoginFormContext.CONNECTION || isEmailNotConnected || isEmailNotVerified"
      >
        <div class="mb-4">
          <div class="v-field--variant-outlined relative">
            <input
              v-model.trim="emailInput"
              v-focus
              :class="{ 'is-error': errors.email.size > 0 }"
              type="text"
              name="email"
              :placeholder="$t('homepage.email')"
              :disabled="isLoading"
            />
            <div class="v-field__outline" v-html="inputLabel('homepage.email')" />
          </div>
          <div class="validation-text__wrapper">
            <div
              class="validation-text"
              :class="{ 'validation-text--error': errors.email.size > 0 }"
            >
              <span v-for="(error, i) in errors.email" :key="i">
                {{ error }}
              </span>
            </div>
          </div>
        </div>
        <app-button
          type="submit"
          btn-type="confirm"
          :disabled="isLoading || email.length === 0 || errors.email.size > 0"
          @click="$emit('continue')"
        >
          {{ $t('homepage.continue') }}
          <component-loading class="absolute inset-0" :is-loading="isLoading" />
        </app-button>
      </div>
      <div v-else>
        <p class="mb-6 text-texts-standard-important font-medium text-36">
          {{ user.email }}
        </p>
        <div class="email-connect text-28 px-4">
          <div class="flexing flex-row gap-4">
            <app-icon class="email-connect-icon" icon-name="mail-light" />
            <div
              class="email-connect-title"
              :class="{
                'font-bold': $isWsm,
                'font-medium': $isSsm,
              }"
              v-html="getConnectedAccountStatusText(connectedAccounts?.email)"
            />
          </div>
          <app-icon
            v-if="connectedAccounts?.email === ConnectedAccountStatus.Connected"
            icon-name="done-sm"
          />
        </div>
      </div>

      <div class="use-text flexing text-texts-standard-default uppercase m-auto text-28">
        <div class="use-text__divider use-text__divider--left" />
        <span
          v-if="context !== LoginFormContext.CONNECTION || !allSocialConnected"
          class="use-text__label"
        >
          {{ $t('homepage.orUse') }}
        </span>
        <div class="use-text__divider use-text__divider--right" />
      </div>
      <div class="flex flex-col w-full space-y-6">
        <facebook-button
          :is-disabled="isLoading"
          :monochrome="
            context !== LoginFormContext.CONNECTION ||
            connectedAccounts?.facebook === ConnectedAccountStatus.NotConnected
          "
          :status="connectedAccounts?.facebook"
          :context="context"
          @facebook-connection="onFacebookConnection"
        />
        <google-button
          :is-disabled="isLoading"
          :status="connectedAccounts?.google"
          :context="context"
          @google-connection="onGoogleConnection"
        />
      </div>
    </form>
    <div class="continue-policy__wrapper" :class="`continue-policy__wrapper--${context}`">
      <p v-if="context === 'login'" v-html="registrationText" />
    </div>
  </div>
</template>

<script lang="ts">
import ComponentLoading from '../GlobalComponents/ComponentLoading.vue'
import FacebookButton from './FacebookButton.vue'
import GoogleButton from './GoogleButton.vue'
import { getConnectedAccountStatusText } from '@/helpers'
import { defineComponent, type PropType } from 'vue'
import { ConnectedAccountStatus, LoginFormContext } from '@/enums/LoginForm'
import { mapActions, mapState } from 'pinia'
import { useAccountStore } from '@/store/pinia/accountStore'
import { useUserStore } from '@/store/pinia/userStore'
import { AGE_REQUIRED } from '@/data/login/constants'
import type { FormErrors } from '@/interfaces/login/Login'

export interface ComponentData {
  ConnectedAccountStatus: typeof ConnectedAccountStatus
  LoginFormContext: typeof LoginFormContext
  AGE_REQUIRED: typeof AGE_REQUIRED
  emailInput: string
}

export default defineComponent({
  components: {
    ComponentLoading,
    FacebookButton,
    GoogleButton,
  },
  props: {
    email: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object as PropType<FormErrors>,
      default: () => {},
    },
    context: {
      type: String as PropType<LoginFormContext>,
      default: '',
      validator: (value: LoginFormContext) => Object.values(LoginFormContext).includes(value),
    },
  },
  emits: ['update:email', 'continue'],
  data(): ComponentData {
    return {
      ConnectedAccountStatus,
      LoginFormContext,
      AGE_REQUIRED,
      emailInput: this.email,
    }
  },
  computed: {
    ...mapState(useAccountStore, ['connectedAccounts']),
    ...mapState(useUserStore, { user: 'getUserData', userLanguage: 'getLanguage' }),
    isEmailNotConnected(): boolean {
      return (
        this.context === LoginFormContext.CONNECTION &&
        this.connectedAccounts?.email === ConnectedAccountStatus.NotConnected
      )
    },
    isEmailNotVerified(): boolean {
      return (
        this.context === LoginFormContext.CONNECTION &&
        this.connectedAccounts?.email === ConnectedAccountStatus.VerificationPending
      )
    },
    allSocialConnected(): boolean {
      return (
        this.connectedAccounts?.facebook !== ConnectedAccountStatus.NotConnected &&
        this.connectedAccounts?.google !== ConnectedAccountStatus.NotConnected
      )
    },
    languageCode(): string {
      if (this.userLanguage)
        return this.userLanguage === 'sk' || this.userLanguage === 'cs' ? 'sk' : 'en'

      return localStorage.getItem('language') === 'sk' || localStorage.getItem('language') === 'cs'
        ? 'sk'
        : 'en'
    },
    registrationText(): string {
      let text = this.$t('homepage.registrationText')

      text = this.$replacePlaceholder(text, /%s/gi, this.AGE_REQUIRED.toString())

      text = this.$replacePlaceholder(
        text,
        /{link1}/gi,
        `<a target="_blank" class="clickable-element text-texts-standard-important underline" href="https://www.powerplay.studio/${this.languageCode}/license/">`,
      )

      text = this.$replacePlaceholder(
        text,
        /{link2}/gi,
        `<a target="_blank" class="clickable-element text-texts-standard-important underline" href="https://www.powerplay.studio/${this.languageCode}/privacy-policy/">`,
      )

      text = this.$replacePlaceholder(
        text,
        /{link3}/gi,
        '<a class="clickable-element text-texts-standard-important underline" href="?rules=true">',
      )

      text = this.$replacePlaceholder(text, /{\/link}/gi, '</a>')

      return text
    },
  },
  watch: {
    emailInput(): void {
      this.$emit('update:email', this.emailInput)
    },
  },
  methods: {
    ...mapActions(useAccountStore, ['loadConnectedAccounts']),
    getConnectedAccountStatusText,
    inputLabel(translatedLabel: string): string {
      return `
        <div class="v-field__outline__start"></div>
        <div class="v-field__outline__notch">
          <label class="v-label v-field-label" aria-hidden="true">
            ${this.$t(translatedLabel)}
          </label>
        </div>
        <div class="v-field__outline__end"></div>
      `
    },
    async onFacebookConnection(): Promise<void> {
      await this.loadConnectedAccounts(true)
    },
    async onGoogleConnection(): Promise<void> {
      await this.loadConnectedAccounts(true)
    },
  },
})
</script>
